import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Portal, Tooltip } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { RESIDUAL_VALUE_GRID_NEXT_STATES, RESIDUAL_VALUE_GRID_STATE } from 'features/residualValueGrid/queries';
import { useTranslation } from 'react-i18next';
import { RESIDUAL_VALUE_GRID_STATE_UPDATED } from 'features/residualValueGrid/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation } from 'hooks/apollo/useMutation';
import { UPDATE_RESIDUAL_VALUE_GRID_STATE } from 'features/residualValueGrid/mutations';
import FlexBox from 'components/common/FlexBox';
import ErrorIcon from '@mui/icons-material/Error';
import useQuery from 'hooks/apollo/useQuery';
import useSubscription from 'hooks/apollo/useSubscription';
import { isGridEditableVar } from '../variables';
import { ADMIN_RESIDUAL_VALUE_GRIDS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const ResidualValueGridStateContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const canWrite = usePermission(ADMIN_RESIDUAL_VALUE_GRIDS_WRITE);

  const [initialState, setInitialState] = useState();
  const [currentState, setCurrentState] = useState();

  const [error, setError] = useState();
  const [pending, setPending] = useState(false);

  // #region Load

  useQuery(RESIDUAL_VALUE_GRID_STATE, {
    variables: { id },
    skip: !id,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      setCurrentState(data?.residualValueGridState);
      setInitialState(data?.residualValueGridState);
      if (!data?.residualValueGridState?.flowId) setError(t('ResidualValueGrid.NoFlow'));
      else if (!data?.residualValueGridState?.id) setError(t('ResidualValueGrid.NoState'));
    }
  });

  const { data: nextStatesData } = useQuery(RESIDUAL_VALUE_GRID_NEXT_STATES, {
    variables: { stateId: initialState?.id },
    skip: !initialState?.id
  });

  const nextStates = useMemo(() => nextStatesData?.residualValueGridNextStates || [], [nextStatesData]);

  // #endregion

  // #region Save

  const [updateResidualValueGridState] = useMutation(UPDATE_RESIDUAL_VALUE_GRID_STATE, {
    onError: _ => {
      setPending(false);
      setCurrentState(initialState);
    }
  });

  const handleUpdate = useCallback(
    value => {
      setPending(true);
      setCurrentState(value);
      updateResidualValueGridState({
        variables: { id, stateId: value.id }
      });
    },
    [id, updateResidualValueGridState]
  );

  useSubscription(RESIDUAL_VALUE_GRID_STATE_UPDATED, {
    onError: error => addToast(error, 'error'),
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      setPending(false);
      setInitialState(currentState);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'residualValueGrids' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'residualValueGridState', args: { id } });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'residualValueGridStateHistory', args: { id } });
      addToast(t('ResidualValueGrid.StateChanged'), 'success');
      isGridEditableVar(data.data.residualValueGridStateUpdated.residualValueGrid.isEditable);
    }
  });

  // #endregion

  return (
    <Portal container={() => document.getElementById('residual-value-grid-state')}>
      <FlexBox alignItems='end' gap={0.5}>
        <Autocomplete
          label={t('ResidualValueGrid.State')}
          value={currentState}
          onChange={handleUpdate}
          options={nextStates}
          disabled={!canWrite || pending || error}
        />
        {error && (
          <Tooltip title={error}>
            <ErrorIcon color='error' fontSize='small' sx={{ mb: 0.5 }} />
          </Tooltip>
        )}
      </FlexBox>
    </Portal>
  );
};

ResidualValueGridStateContainer.propTypes = {
  id: PropTypes.number
};

export default ResidualValueGridStateContainer;
